/**
 * AgentChatContext - Global state management for agent chat sessions
 *
 * This context provides a way to manage multiple chat sessions with agents
 * from anywhere in the application. It wraps the useMultiChat hook and
 * provides additional functionality for the floating chat window system.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useMultiChat } from '../hooks/useMultiChat.js';

// Create the context
const AgentChatContext = createContext(null);

// Storage key for window states
const WINDOW_STATES_KEY = 'agent_chat_window_states';

/**
 * AgentChatProvider - Provides chat session management to the entire app
 */
export const AgentChatProvider = ({ authToken, children }) => {
  // Use the existing multi-chat hook for session management
  const multiChat = useMultiChat(authToken);

  // Track window states (minimized, open, position)
  const [windowStates, setWindowStates] = useState(() => {
    try {
      const stored = localStorage.getItem(WINDOW_STATES_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load window states:', e);
    }
    return {};
  });

  // Track pending windows that are still connecting to the backend
  const [pendingWindows, setPendingWindows] = useState(new Map());

  // Save window states to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(WINDOW_STATES_KEY, JSON.stringify(windowStates));
    } catch (e) {
      console.warn('Failed to save window states:', e);
    }
  }, [windowStates]);

  // Close a pending chat window
  const closePendingChat = useCallback((pendingId) => {
    setPendingWindows((prev) => {
      const next = new Map(prev);
      next.delete(pendingId);
      return next;
    });
  }, []);

  // Start a new chat session with an agent (opens window immediately)
  const startAgentChat = useCallback(
    async (agent) => {
      if (!agent) {
        console.error('No agent provided');
        return { success: false, error: 'No agent provided' };
      }

      const sessionName = agent.name || 'Agent Chat';
      const pendingId = `pending-${Date.now()}`;

      console.log('Starting agent chat:', { sessionName, agentId: agent.id, pendingId });

      // Immediately show the chat window in a loading state
      setPendingWindows((prev) => {
        const next = new Map(prev);
        next.set(pendingId, {
          agentId: agent.id,
          agentName: agent.name,
          agentType: agent.type,
          loadingStatus: 'Creating session...',
          isMinimized: false,
        });
        return next;
      });

      // Update status after a short delay to show progress
      const statusTimer = setTimeout(() => {
        setPendingWindows((prev) => {
          const entry = prev.get(pendingId);
          if (!entry) return prev;
          const next = new Map(prev);
          next.set(pendingId, { ...entry, loadingStatus: 'Connecting to agent...' });
          return next;
        });
      }, 800);

      // Now start the real session in the background
      const result = await multiChat.startNewSession(null, sessionName, agent);

      clearTimeout(statusTimer);

      // Remove the pending window
      setPendingWindows((prev) => {
        const next = new Map(prev);
        next.delete(pendingId);
        return next;
      });

      if (result.success) {
        // Set window state to open with the real session ID
        setWindowStates((prev) => ({
          ...prev,
          [result.sessionId]: {
            isMinimized: false,
            isOpen: true,
            agentId: agent.id,
            agentName: agent.name,
            agentType: agent.type,
            position: Object.keys(prev).length * 320,
          },
        }));
      }

      return result;
    },
    [multiChat],
  );

  // Toggle minimize/expand for a session window
  const toggleMinimize = useCallback((sessionId) => {
    setWindowStates((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        isMinimized: !prev[sessionId]?.isMinimized,
      },
    }));
  }, []);

  // Close a chat window (end the session)
  const closeChat = useCallback(
    async (sessionId) => {
      const result = await multiChat.endSession(sessionId);

      if (result.success) {
        setWindowStates((prev) => {
          const newStates = { ...prev };
          delete newStates[sessionId];
          return newStates;
        });
      }

      return result;
    },
    [multiChat],
  );

  // Get all open chat windows with their states
  const getOpenChats = useCallback(() => {
    const sessions = multiChat.getAllSessions();
    return sessions
      .filter((session) => windowStates[session.id]?.isOpen)
      .map((session) => ({
        ...session,
        windowState: windowStates[session.id] || { isMinimized: false, isOpen: true },
      }));
  }, [multiChat, windowStates]);

  // Get window state for a specific session
  const getWindowState = useCallback(
    (sessionId) => {
      return windowStates[sessionId] || { isMinimized: false, isOpen: false };
    },
    [windowStates],
  );

  // Send a message to a specific session
  const sendMessageToSession = useCallback(
    async (sessionId, message) => {
      // First switch to the session
      multiChat.switchSession(sessionId);
      // Then send the message using streaming
      return await multiChat.sendMessageStream(message);
    },
    [multiChat],
  );

  // Get session by ID
  const getSession = useCallback(
    (sessionId) => {
      return multiChat.chatSessions.get(sessionId);
    },
    [multiChat.chatSessions],
  );

  // Check if there are any open chat windows
  const hasOpenChats = useCallback(() => {
    return Object.values(windowStates).some((state) => state.isOpen);
  }, [windowStates]);

  // Count open chat windows
  const openChatCount = useCallback(() => {
    return Object.values(windowStates).filter((state) => state.isOpen).length;
  }, [windowStates]);

  const value = {
    // Multi-chat hook values
    ...multiChat,

    // Agent chat specific functions
    startAgentChat,
    toggleMinimize,
    closeChat,
    closePendingChat,
    getOpenChats,
    getWindowState,
    sendMessageToSession,
    getSession,
    hasOpenChats,
    openChatCount,
    windowStates,
    pendingWindows,
  };

  return <AgentChatContext.Provider value={value}>{children}</AgentChatContext.Provider>;
};

/**
 * useAgentChat - Hook to access the agent chat context
 */
export const useAgentChat = () => {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
};

export default AgentChatContext;
